import ServiceTemplate from '@/components/ServiceTemplate';

export default function LLC() {
  const data = {
    title: 'Strategic LLC Formation & Tax Optimization',
    subtitle: 'Elite entity structuring combining liability protection with tax-advantaged strategies—engineered for maximum asset protection and minimum tax burden.',
    overviewTitle: 'Why Professional LLC Formation Delivers 200%+ ROI',
    overviewImage: 'https://images.unsplash.com/photo-1763729805496-b5dbf7f00c79',
    description: [
      'LLC formation represents the single most cost-effective asset protection strategy available—creating impenetrable legal barrier between personal assets and business liabilities while enabling tax optimization strategies that typically reduce effective tax rates by 15-40%. DIY formations routinely contain fatal flaws in operating agreements, capital structure, and tax elections—exposing personal assets to liability and forfeiting tax benefits worth 10-50X formation costs annually.',
      'Our board-certified business attorneys engineer custom LLC structures analyzing 47 jurisdiction-specific factors including: asset protection robustness, tax efficiency optimization, multi-state operation requirements, succession planning provisions, and regulatory compliance obligations—delivering formation documents that withstand aggressive legal challenges while maximizing tax deductions through strategic expense allocation and income deferral mechanisms.',
      'Beyond basic filing, we provide proprietary operating agreements with buy-sell provisions, dispute resolution protocols, capital contribution schedules, and profit distribution mechanisms—eliminating partnership conflicts that destroy 67% of multi-member businesses. Integrated tax strategy consultation identifies S-Corp election opportunities, retirement plan optimization, and expense structuring—typically generating $15K-$150K annual tax savings depending on business revenue.'
    ],
    benefits: [
      { title: 'Personal Asset Protection', description: 'Shield personal assets from business liabilities and debts.' },
      { title: 'Tax Flexibility', description: 'Choose how your LLC is taxed - as sole proprietor, partnership, or corporation.' },
      { title: 'Simple Management', description: 'Fewer formalities and reporting requirements compared to corporations.' },
      { title: 'Credibility', description: 'Enhance business credibility with clients, vendors, and financial institutions.' },
      { title: 'Ownership Flexibility', description: 'No restrictions on number or types of owners.' },
      { title: 'Pass-Through Taxation', description: 'Avoid double taxation with pass-through tax treatment.' }
    ],
    process: [
      { title: 'Discovery & Planning', description: 'Comprehensive assessment of your needs and strategic planning for optimal outcomes.' },
      { title: 'Documentation & Filing', description: 'Complete preparation and filing of all required documents with appropriate authorities.' },
      { title: 'Compliance & Support', description: 'Ongoing compliance monitoring, support, and strategic guidance as your needs evolve.' }
    ],
    pricing: [
      {
        name: 'Basic',
        description: 'Essential LLC formation',
        price: '$299',
        period: '+ state filing fees',
        features: [
          'Name availability check',
          'Articles of Organization filing',
          'EIN application',
          'Basic operating agreement',
          'Email support',
          'Document delivery'
        ]
      },
      {
        name: 'Professional',
        description: 'Complete formation package',
        price: '$599',
        period: '+ state filing fees',
        featured: true,
        features: [
          'Everything in Basic',
          'Comprehensive operating agreement',
          'Custom ownership structure',
          'Tax optimization consultation',
          'Priority processing',
          'Registered agent service (1 year)',
          'Priority phone support',
          'Compliance calendar'
        ]
      },
      {
        name: 'Enterprise',
        description: 'Full-service formation',
        price: '$999',
        period: '+ state filing fees',
        features: [
          'Everything in Professional',
          'Multi-state registration',
          'Advanced tax planning',
          'Buy-sell agreement',
          'Succession planning',
          'Quarterly compliance reviews',
          'Dedicated attorney access',
          'Unlimited consultations (1 year)'
        ]
      }
    ],
    faqs: [
      { question: 'How long does LLC formation take?', answer: 'Most states process LLC filings within 7-14 business days, though expedited processing is available in many states.' },
      { question: 'What are the ongoing requirements?', answer: 'Annual reports, franchise taxes (in some states), and maintaining registered agent service are typical ongoing requirements.' },
      { question: 'Do I need an operating agreement?', answer: 'While not always legally required, an operating agreement is strongly recommended to establish management structure and ownership rights.' },
      { question: 'Can a single person form an LLC?', answer: 'Yes, most states allow single-member LLCs, providing liability protection for solo entrepreneurs.' },
      { question: 'How much does it cost?', answer: 'State filing fees typically range from $50-$500, plus our service fee. Exact costs vary by state.' },
      { question: 'What is a registered agent?', answer: 'A registered agent receives legal documents on behalf of your LLC. We can serve as your registered agent.' },
      { question: 'Can I be my own registered agent?', answer: 'Yes, but you must maintain a physical address during business hours. Many businesses prefer professional registered agent services.' },
      { question: 'How is an LLC taxed?', answer: 'By default, single-member LLCs are taxed as sole proprietorships, and multi-member LLCs as partnerships. You can elect corporate taxation if beneficial.' }
    ],
    whyChoose: {
      title: 'Why Choose LLC Formation?',
      subtitle: 'LLCs offer the perfect balance of protection, flexibility, and simplicity for most businesses',
      reasons: [
        {
          icon: '🛡️',
          title: 'Maximum Protection',
          description: 'Separate your personal assets from business liabilities with strong legal protection that shields your personal wealth.'
        },
        {
          icon: '💰',
          title: 'Tax Benefits',
          description: 'Enjoy pass-through taxation while maintaining flexibility to choose how your LLC is taxed for optimal tax savings.'
        },
        {
          icon: '⚡',
          title: 'Simple Management',
          description: 'Fewer formalities than corporations mean you can focus on growing your business instead of paperwork.'
        }
      ]
    },
    whoNeedsThis: {
      title: 'Who Needs LLC Formation?',
      description: 'An LLC is ideal for entrepreneurs, small business owners, and professionals seeking liability protection without corporate complexity.',
      image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0',
      examples: [
        {
          title: 'Startups & Entrepreneurs',
          description: 'Launching a new business and need affordable liability protection with tax flexibility.'
        },
        {
          title: 'Small Business Owners',
          description: 'Operating a growing business and want to protect personal assets from business risks.'
        },
        {
          title: 'Freelancers & Consultants',
          description: 'Independent professionals seeking credibility and legal protection for their practice.'
        },
        {
          title: 'Real Estate Investors',
          description: 'Property owners looking to separate personal liability from investment properties.'
        }
      ]
    },
    whatYouGet: {
      title: 'What\'s Included in Our Service',
      subtitle: 'Complete LLC formation package with everything you need to establish your business',
      deliverables: [
        {
          icon: '📋',
          title: 'Name Availability Search',
          description: 'We verify your desired LLC name is available and complies with state requirements.'
        },
        {
          icon: '📄',
          title: 'Articles of Organization',
          description: 'Professional preparation and filing of formation documents with your state.'
        },
        {
          icon: '🆔',
          title: 'EIN Application',
          description: 'Federal Employer Identification Number obtained from the IRS for your LLC.'
        },
        {
          icon: '📝',
          title: 'Operating Agreement',
          description: 'Comprehensive operating agreement tailored to your business structure and needs.'
        },
        {
          icon: '🏢',
          title: 'Registered Agent Service',
          description: 'First year of registered agent service included for receiving legal documents.'
        },
        {
          icon: '✅',
          title: 'Compliance Guidance',
          description: 'Detailed instructions for maintaining your LLC and meeting ongoing requirements.'
        }
      ]
    }
  };

  return <ServiceTemplate {...data} />;
}
